import { useMemo } from "react";

import { graphql } from "lib/generated/app-service-gql";
import { FeatureEntityType } from "lib/types/graphQLEnums";
import { useGraphQlQuery } from "../useGraphQlClient";
import { UsageFilters, usageWindow } from "./usageWindow";

const companyUserUsageQuery = graphql(`
  query companyUserUsage($request: CompanyUserUsageRequest!) {
    companyUserUsage(request: $request) {
      users {
        userId
        firstName
        lastName
        email
        teamName
        lastActiveAt
        activeDays
        mcpDays
        features {
          entityType
          daysUsed
        }
      }
    }
  }
`);

export type UserUsage = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  teamName: string;
  /** The later of their latest page load and latest MCP use, ever; null when neither exists. */
  lastActiveAt: string | null;
  /** Days of the window they were active in the app or through MCP. */
  activeDays: number;
  /** Days of the window they used Stotles through MCP. */
  mcpDays: number;
  /** Days of the window they used each feature, zero for those they didn't or the page hides. */
  daysUsed: Record<FeatureEntityType, number>;
};

export type CompanyUserUsage = {
  /** Every user the counts are drawn from, by name. */
  users: UserUsage[];
  isLoading: boolean;
  isError: boolean;
};

/**
 * How each of the company's users used Stotles in the last `days` UTC days, today included, with
 * notice use as one feature or split by the notice's stage.
 *
 * The server leaves out features a user didn't use; the table has a column for every one, so
 * they're filled in here. Keyed on the UTC day, as the charts are, so a cached window never
 * outlives it.
 */
export function useCompanyUserUsage(
  filters: UsageFilters,
  splitNoticesByStage: boolean,
): CompanyUserUsage {
  const { today } = usageWindow(filters.days);
  // Left out when off, as the team and payment type are when unset.
  const request = { ...filters, splitNoticesByStage: splitNoticesByStage || undefined };
  const { data, isLoading, isError } = useGraphQlQuery(
    ["companyUserUsage", request, today],
    companyUserUsageQuery,
    [{ request }],
  );

  // Memoised on the response: the table re-filters, and goes back to its first page, only when
  // the data changes.
  const users = useMemo(
    () =>
      (data?.companyUserUsage.users ?? []).map(({ features, ...user }) => {
        const daysUsedByType = new Map(
          features.map((feature) => [feature.entityType, feature.daysUsed]),
        );
        const daysUsed = Object.fromEntries(
          Object.values(FeatureEntityType).map((entityType) => [
            entityType,
            daysUsedByType.get(entityType) ?? 0,
          ]),
        ) as Record<FeatureEntityType, number>;
        return { ...user, lastActiveAt: user.lastActiveAt ?? null, daysUsed };
      }),
    [data],
  );

  return { users, isLoading, isError };
}
